import { useCallback, useEffect, useRef, useState } from "react";
import { FeatureAvailability, FeatureFlags } from "../featureAvailability";
import { featureAvailabilityService } from "../services/featureAvailabilityService";

type State = { scope: string; data: FeatureAvailability | null; error: string | null };

export function useFeatureAvailability(audience: string, path: string, authenticated: boolean) {
  const scope = `${audience}:${path}:${authenticated}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const generation = useRef(0);
  const [state, setState] = useState<State>({ scope: "", data: null, error: null });
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!authenticated) return;
    let active = true;
    const load = async () => {
      const request = ++generation.current;
      try {
        const data = await featureAvailabilityService.get(audience);
        if (active && request === generation.current) setState({ scope, data, error: null });
      } catch (error) {
        if (active && request === generation.current) {
          setState({ scope, data: null, error: error instanceof Error ? error.message : "Unable to load feature availability." });
        }
      }
    };
    void load();
    // Recheck when returning to an open tab so another admin's changes take effect.
    const onFocus = () => { void load(); };
    window.addEventListener("focus", onFocus);
    return () => { active = false; window.removeEventListener("focus", onFocus); };
  }, [audience, scope, authenticated, retry]);

  const reload = useCallback(() => {
    setState({ scope: "", data: null, error: null });
    setRetry((value) => value + 1);
  }, []);

  const save = async (features: FeatureFlags) => {
    // A read that began before this write must never replace the saved settings.
    ++generation.current;
    const data = await featureAvailabilityService.save(audience, features);
    if (currentScope.current === scope) {
      ++generation.current;
      setState({ scope, data, error: null });
    }
  };

  const current = state.scope === scope && authenticated;
  return {
    data: current ? state.data : null,
    error: current ? state.error : null,
    reload,
    save,
  };
}
